import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
} from 'lightweight-charts';
import {
  LAYOUT_PRIORITY,
  MarkerLayoutClient,
  type LayoutContext,
  type LayoutItem,
  type MarkerLayoutRegistry,
} from './markerLayout';

export interface SkippedMarker {
  time: number; // timestamp in ms
  price: number; // candle high or low
  color: string;
  isBullish: boolean; // determines if marker is below (bullish) or above (bearish) the candle
}

interface SkippedMarkerRendererData {
  x: number;
  y: number;
  color: string;
}

class SkippedMarkerPaneRenderer implements IPrimitivePaneRenderer {
  private _data: SkippedMarkerRendererData[] = [];
  private _size: number = 6; // Square size in pixels

  update(data: SkippedMarkerRendererData[], size: number): void {
    this._data = data;
    this._size = size;
  }

  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const pixelRatio = scope.horizontalPixelRatio as number;
      const scaledSize = this._size * pixelRatio;
      const halfSize = scaledSize / 2;

      for (const marker of this._data) {
        const x = marker.x * pixelRatio;
        const y = marker.y * pixelRatio;
        ctx.fillStyle = marker.color;
        ctx.fillRect(
          x - halfSize,
          y - halfSize,
          scaledSize,
          scaledSize
        );
      }
    });
  }
}

class SkippedMarkerPaneView implements IPrimitivePaneView {
  private _source: SkippedMarkerPrimitive;
  private _renderer: SkippedMarkerPaneRenderer;

  constructor(source: SkippedMarkerPrimitive) {
    this._source = source;
    this._renderer = new SkippedMarkerPaneRenderer();
  }

  zOrder(): PrimitivePaneViewZOrder {
    // 'top' ensures markers render above candles
    return 'top';
  }

  renderer(): IPrimitivePaneRenderer {
    const data = this._source.getRendererData();
    this._renderer.update(data, this._source.getSize());
    return this._renderer;
  }
}

export class SkippedMarkerPrimitive implements ISeriesPrimitive<Time> {
  private _markers: SkippedMarker[] = [];
  private _paneView: SkippedMarkerPaneView;
  private _requestUpdate?: () => void;
  private _series?: SeriesAttachedParameter<Time>['series'];
  private _chart?: SeriesAttachedParameter<Time>['chart'];
  private _size: number = 6; // Default square size
  private _offset: number = 8; // Offset from candle high/low in pixels
  private _layout = new MarkerLayoutClient('skipped', (ctx) => this._layoutItems(ctx));

  constructor() {
    this._paneView = new SkippedMarkerPaneView(this);
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._series = param.series;
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
    this._layout.setAttached(true);
  }

  detached(): void {
    this._layout.setAttached(false);
    this._series = undefined;
    this._chart = undefined;
    this._requestUpdate = undefined;
  }

  /** Join the chart's shared marker layout (null: fixed offset alone). */
  setMarkerLayout(reg: MarkerLayoutRegistry | null): void {
    this._layout.setRegistry(reg);
  }

  private _layoutItems(ctx: LayoutContext): LayoutItem[] {
    const out: LayoutItem[] = [];
    const side = this._size + 2;
    this._markers.forEach((marker, i) => {
      const t = Math.floor(marker.time / 1000);
      if (t < ctx.fromTime || t > ctx.toTime) return;
      const x = ctx.timeToX(t);
      const y = ctx.priceToY(marker.price);
      if (x === null || y === null) return;
      out.push({
        key: `${t}:${marker.isBullish ? 'b' : 's'}:${i}`,
        time: t,
        x,
        side: marker.isBullish ? 'below' : 'above',
        anchorY: y,
        w: side,
        h: side,
        gap: 3,
        priority: LAYOUT_PRIORITY.skipped,
        color: marker.color,
        label: 'skipped signal',
        ref: marker,
      });
    });
    return out;
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._paneView];
  }

  updateAllViews(): void {
    // Called when chart needs to update views
  }

  setMarkers(markers: SkippedMarker[]): void {
    this._markers = markers;
    this._layout.invalidate();
    this._requestUpdate?.();
  }

  setSize(size: number): void {
    this._size = size;
    this._layout.invalidate();
    this._requestUpdate?.();
  }

  setOffset(offset: number): void {
    this._offset = offset;
    this._requestUpdate?.();
  }

  getSize(): number {
    return this._size;
  }

  getRendererData(): SkippedMarkerRendererData[] {
    if (!this._series || !this._chart) return [];

    const shared = this._layout.placed();
    if (shared) {
      return shared.map((p) => ({ x: p.item.x, y: p.top + p.h / 2, color: (p.item.ref as SkippedMarker).color }));
    }
    const timeScale = this._chart.timeScale();
    const data: SkippedMarkerRendererData[] = [];

    for (const marker of this._markers) {
      // Convert timestamp to Time format (seconds)
      const timeInSeconds = Math.floor(marker.time / 1000) as Time;

      // Get x coordinate from time
      const x = timeScale.timeToCoordinate(timeInSeconds);
      if (x === null) continue;

      // Get y coordinate from price with offset
      // For bullish (below bar), we subtract offset to move marker down
      // For bearish (above bar), we add offset to move marker up (in screen coords, up is lower y)
      const priceY = this._series.priceToCoordinate(marker.price);
      if (priceY === null) continue;

      // Apply pixel offset
      const y = marker.isBullish
        ? priceY + this._offset  // Below candle (lower on screen = higher y)
        : priceY - this._offset; // Above candle (higher on screen = lower y)

      data.push({
        x,
        y,
        color: marker.color,
      });
    }

    return data;
  }
}
